<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router/composables'
import paypalIcon from '@/assets/paypal.png'
import stripeIcon from '@/assets/stripe.png'
import {
  createShowPaymentMethodSelector,
  usePurchase,
} from '@/composables/payment'
import { PaymentMethodEnum } from '@/composables/payment/constants'
import { useAxonViewItem } from '@/composables/axon/useAxonViewItem'
import { useFbqAddToCart } from '@/composables/fbq/useFbqAddToCart'
import { useUserStore } from '@/stores'

const props = defineProps<{
  config: any
}>()

const router = useRouter()
const userStore = useUserStore()
const coinBalance = computed(() => userStore.coinBalance || 0)

const {
  templateProducts,
  PayTemplate,
  currentPaymentMethod,
  availablePaymentMethods,
  selectPaymentMethod,
} = usePurchase('store')

const showPaymentMethodSelector = createShowPaymentMethodSelector(availablePaymentMethods)

const { reportViewItem } = useAxonViewItem()
watch(templateProducts, (val) => {
  if (val.length)
    reportViewItem(val, currentPaymentMethod.value, 'Coins Package')
}, { immediate: true })

const { reportAddToCart } = useFbqAddToCart()
watch(templateProducts, (val) => {
  for (const p of val)
    reportAddToCart(p.productId, 'Coins Package', p.price ?? 0)
})

const selectedProductId = ref<string | null>(null)

watch(templateProducts, (val) => {
  if (!val.length) {
    selectedProductId.value = null
    return
  }

  const currentExists = val.some(item => item.productId === selectedProductId.value)
  if (!currentExists)
    selectedProductId.value = val[0].productId
}, { immediate: true })

const selectedProduct = computed(() => {
  if (!selectedProductId.value)
    return templateProducts.value[0] || null

  return templateProducts.value.find(item => item.productId === selectedProductId.value) || templateProducts.value[0] || null
})

const paymentMethods = computed(() => {
  return (availablePaymentMethods.value || []).map((method) => {
    const paymentMethod = Number(method.paymentMethod)
    return {
      value: paymentMethod,
      icon: paymentMethod === PaymentMethodEnum.PAYPAL ? paypalIcon : stripeIcon,
      alt: paymentMethod === PaymentMethodEnum.PAYPAL ? 'PayPal' : 'Stripe',
    }
  })
})

const totalCoins = computed(() => {
  if (!selectedProduct.value)
    return 0
  return Number(selectedProduct.value.coinsBuy) + Number(selectedProduct.value.coinsBonus)
})

function formatPrice(price?: number | string) {
  const numericPrice = Number(price || 0)
  return numericPrice.toFixed(2)
}

function formatBonusRate(item: any) {
  if (!item?.coinsBuy)
    return '+0% Bonus'

  return `+${Math.round((Number(item.coinsBonus || 0) / Number(item.coinsBuy)) * 100)}% Bonus`
}

function formatBonusText(item: any) {
  return `+${Number(item?.coinsBonus || 0)} Bonus`
}

function openContactUs() {
  router.push({ name: 'contact-us' })
}

function selectProduct(item: any) {
  selectedProductId.value = item.productId
}

function handlePayNow() {
  if (!selectedProduct.value)
    return

  PayTemplate(selectedProduct.value)
}

// 可配置的颜色
const defaultColors = {
  pageBg: '#fdf2f8',
  cardBg: '#fff',
  cardBorder: '#e5e7eb',
  cardActiveBorder: '#ec3a9c',
  cardActiveShadow: 'rgba(236, 58, 156, 0.16)',
  cardRightBg: '#fdf2f8',
  bonusPillBg: '#fde7f2',
  bonusPillText: '#ec3a9c',
  bonusRateText: '#ec3a9c',
  bottomBarBg: 'rgba(255, 255, 255, 0.98)',
  bottomBarBorder: '#e5e7eb',
  totalHighlight: '#ec3a9c',
  payBtnGradient: 'linear-gradient(90deg, #FF81B0 0%, #EE3BA4 100%)',
  payBtnShadow: 'rgba(236, 58, 156, 0.22)',
  tagGradient: 'linear-gradient(90deg, #FF4754 0%, #FEA94C 100%)',
}

const payColors = computed(() => {
  return { ...defaultColors, ...props.config.theme?.colors }
})
</script>

<template>
  <div class="storlyn-pay-page" :style="{ background: payColors.pageBg }">
    <BasicNavbar class="storlyn-navbar text-n-text-1" />

    <section class="storlyn-section storlyn-section-first">
      <h2 class="storlyn-section-title text-n-text-1">
        BALANCE: {{ coinBalance }} COINS
      </h2>
    </section>

    <section v-if="showPaymentMethodSelector" class="storlyn-section">
      <h2 class="storlyn-section-title text-n-text-1">
        PAYMENT METHOD
      </h2>
      <div class="payment-method-grid">
        <button
          v-for="method in paymentMethods"
          :key="method.value"
          type="button"
          class="payment-method-card"
          :class="{ active: Number(currentPaymentMethod) === Number(method.value) }"
          :style="{
            background: payColors.cardBg,
            borderColor: payColors.cardBorder,
          }"
          @click="selectPaymentMethod(method.value)"
        >
          <img :src="method.icon" :alt="method.alt" class="payment-method-icon">
        </button>
      </div>
    </section>

    <section class="storlyn-section storlyn-topup-section">
      <h2 v-if="showPaymentMethodSelector" class="storlyn-section-title text-n-text-1">
        TOP UP
      </h2>

      <button
        v-for="item in templateProducts"
        :key="item.productId"
        type="button"
        class="topup-card"
        :class="{ active: selectedProduct?.productId === item.productId }"
        :style="{
          background: payColors.cardBg,
          borderColor: selectedProduct?.productId === item.productId ? payColors.cardActiveBorder : payColors.cardBorder,
          boxShadow: selectedProduct?.productId === item.productId
            ? `0 14px 32px ${payColors.cardActiveShadow}`
            : `0 10px 28px rgba(31, 41, 55, 0.08)`,
        }"
        @click="selectProduct(item)"
      >
        <div v-if="item.cornerTip === 1 && item.marketingText" class="topup-tag" :style="{ background: payColors.tagGradient }">
          {{ item.marketingText }}
        </div>

        <div class="topup-card-left">
          <div class="topup-main-line">
            <span class="topup-coins text-n-text-1">{{ item.coinsBuy }}</span>
            <span class="topup-unit text-n-text-2">{{ config.texts.coins }}</span>
          </div>
          <div class="topup-bonus-pill" :style="{ background: payColors.bonusPillBg, color: payColors.bonusPillText }">
            {{ formatBonusText(item) }}
          </div>
        </div>

        <div class="topup-card-right" :style="{ background: payColors.cardRightBg }">
          <div class="topup-price text-n-text-1">
            US$ {{ formatPrice(item.price) }}
          </div>
          <div class="topup-bonus-rate" :style="{ color: payColors.bonusRateText }">
            {{ formatBonusRate(item) }}
          </div>
        </div>
      </button>
    </section>

    <div class="storlyn-tips">
      <p class="storlyn-tips-title text-n-text-3">
        {{ config.texts.tips }}
      </p>
      <p class="storlyn-tips-line text-n-text-3">
        {{ config.texts.tip1 }}
      </p>
      <p class="storlyn-tips-line text-n-text-3">
        {{ config.texts.tip2 }}
      </p>
      <p class="storlyn-tips-line text-n-text-3">
        {{ config.texts.tip3 }}
      </p>
    </div>

    <button type="button" class="storlyn-help-link text-n-text-2" @click="openContactUs">
      Need help? Please visit our Q&amp;A
    </button>

    <div class="storlyn-bottom-bar" :style="{ background: payColors.bottomBarBg, borderColor: payColors.bottomBarBorder }">
      <p v-if="selectedProduct" class="storlyn-total-text text-n-text-1">
        Total <span class="storlyn-total-highlight" :style="{ color: payColors.totalHighlight }">{{ totalCoins }}</span>
        = {{ selectedProduct.coinsBuy }} {{ props.config.texts.coins }} + {{ selectedProduct.coinsBonus }} Bonus
      </p>
      <button
        type="button"
        class="storlyn-pay-btn"
        :style="{
          background: payColors.payBtnGradient,
          boxShadow: `0 14px 28px ${payColors.payBtnShadow}`,
        }"
        @click="handlePayNow"
      >
        Pay Now
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.storlyn-pay-page {
  min-height: 100dvh;
  padding-bottom: calc(160px + env(safe-area-inset-bottom));
}

.storlyn-section {
  padding: 0 20px;
}

.storlyn-section-first {
  margin-top: 20px;
}

.storlyn-topup-section {
  margin-top: 22px;
}

.storlyn-section-title {
  margin: 0 0 12px;
  font-size: 15px;
  line-height: 1.2;
  font-weight: 800;
  letter-spacing: 0.02em;
}

.payment-method-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.payment-method-card {
  height: 48px;
  border-radius: 7px;
  border: 1px solid;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 12px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.payment-method-card.active {
  border-color: var(--un-preset-theme-colors-n-primary);
  box-shadow: 0 10px 26px rgba(142, 71, 255, 0.18);
}

.payment-method-icon {
  width: 100%;
  max-width: 128px;
  height: 28px;
  object-fit: contain;
}

.topup-card {
  position: relative;
  width: 100%;
  margin-bottom: 12px;
  border-radius: 9px;
  border: 1px solid;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 124px;
  text-align: left;
  overflow: hidden;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.topup-card-left,
.topup-card-right {
  min-height: 88px;
  padding: 16px 14px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.topup-card-right {
  align-items: flex-end;
  text-align: right;
}

.topup-main-line {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 6px;
}

.topup-coins {
  font-size: 22px;
  line-height: 1;
  font-weight: 800;
}

.topup-unit {
  font-size: 12px;
  line-height: 1.2;
  font-weight: 500;
}

.topup-bonus-pill {
  margin-top: 12px;
  align-self: flex-start;
  border-radius: 999px;
  font-size: 10px;
  line-height: 1;
  font-weight: 700;
  padding: 6px 10px;
}

.topup-price {
  font-size: 14px;
  line-height: 1.2;
  font-weight: 500;
}

.topup-bonus-rate {
  margin-top: 10px;
  font-size: 12px;
  line-height: 1.1;
  font-weight: 800;
}

.topup-tag {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  height: 16px;
  padding: 0 9px;
  display: inline-flex;
  align-items: center;
  border-radius: 7px 7px 7px 0;
  color: #fff;
  font-size: 10px;
  line-height: 1;
  font-weight: 600;
}

.storlyn-tips {
  padding: 10px 20px 0;
}

.storlyn-tips-title,
.storlyn-tips-line {
  margin: 0;
  font-size: 12px;
  line-height: 1.5;
}

.storlyn-tips-line {
  margin-top: 6px;
}

.storlyn-help-link {
  display: block;
  width: fit-content;
  margin: 28px auto 0;
  border: 0;
  background: transparent;
  font-size: 20px;
  line-height: 1.4;
  text-decoration: underline;
}

.storlyn-bottom-bar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 20;
  border-top: 1px solid;
  padding: 18px 20px calc(20px + env(safe-area-inset-bottom));
  box-shadow: 0 -8px 30px rgba(31, 41, 55, 0.08);
}

.storlyn-total-text {
  margin: 0 0 18px;
  text-align: center;
  font-size: 18px;
  line-height: 1.35;
  font-weight: 600;
}

.storlyn-total-highlight {
  color: var(--un-preset-theme-colors-n-primary);
}

.storlyn-pay-btn {
  width: 100%;
  height: 64px;
  border: 0;
  border-radius: 999px;
  color: #fff;
  font-size: 20px;
  line-height: 1;
  font-weight: 800;
}

@media (max-width: 640px) {
  .storlyn-section {
    padding: 0 16px;
  }

  .storlyn-section-title {
    font-size: 15px;
  }

  .payment-method-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
  }

  .storlyn-topup-section {
    margin-top: 20px;
  }

  .storlyn-tips {
    padding: 8px 16px 0;
  }

  .storlyn-help-link {
    margin-top: 24px;
    font-size: 14px;
  }

  .storlyn-total-text {
    margin-bottom: 14px;
    font-size: 14px;
  }

  .storlyn-pay-btn {
    height: 54px;
    font-size: 18px;
  }
}
</style>
